// src/canvas/ExplorerChartsSummary.jsx
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Stack, Grid, Text, Card, Group, Badge, Paper } from '@mantine/core';
import { IconAlertTriangle, IconTrendingUp, IconShield, IconShieldCheck } from '@tabler/icons-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

// Priority colors (app standard - consistent with RiskLandscape)
const PRIORITY_COLORS = {
  Critical: '#1A1A1A',
  High: '#993333',
  Significant: '#B94D4D',
  Moderate: '#D4AA7D',
  Low: '#B8C2B7',
  Negligible: '#D8D8D8',
  None: '#EBEBEB'
};

// Area colors for pie chart
const AREA_COLORS = ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#d32f2f', '#00796b'];

function toTitleCase(str = '') {
  return String(str)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/Risk$/, '')
    .trim();
}

/**
 * ExplorerChartsSummary - Overview charts and statistics for Explorer assessments
 * Matches ChartsSummaryV2 design with Recharts
 */
export default function ExplorerChartsSummary({ areaStats, explorerRisks, riskAreas, savedControls, modelId }) {
  const { totals, byArea } = areaStats;

  // Priority chart data for bar chart
  const priorityChartData = useMemo(() => {
    const priorities = totals.priorities || {};
    return Object.entries(priorities)
      .filter(([_, count]) => count > 0)
      .map(([name, value]) => ({
        name,
        value,
        fill: PRIORITY_COLORS[name] || '#CCCCCC'
      }));
  }, [totals.priorities]);

  // Area distribution data for pie chart
  const areaDistributionData = useMemo(() => {
    return Object.entries(byArea)
      .filter(([_, data]) => data.riskCount > 0)
      .map(([areaKey, data], index) => ({
        name: toTitleCase(riskAreas[areaKey]?.label || areaKey),
        value: data.riskCount,
        fill: AREA_COLORS[index % AREA_COLORS.length]
      }));
  }, [byArea, riskAreas]);

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper p="sm" shadow="md" withBorder>
          <Text fw={600} size="sm">{label || payload[0]?.name}</Text>
          <Text size="sm" c="dimmed">
            {payload[0]?.value} risks
          </Text>
        </Paper>
      );
    }
    return null;
  };

  return (
    <Stack gap="lg">
      {/* Key Metrics Row */}
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Card withBorder radius="md" p="md" h={120}>
            <Stack gap="xs" align="center" justify="center" h="100%">
              <Group gap="xs" align="center">
                <IconAlertTriangle size={20} style={{ color: PRIORITY_COLORS.Critical }} />
                <Text fw={700} size="xl" style={{ color: PRIORITY_COLORS.Critical }}>
                  {totals.priorities?.Critical || 0}
                </Text>
              </Group>
              <Text size="sm" ta="center" c="dimmed">Critical Risks</Text>
              <Text size="xs" ta="center" style={{ color: PRIORITY_COLORS.Critical }}>Immediate attention</Text>
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Card withBorder radius="md" p="md" h={120}>
            <Stack gap="xs" align="center" justify="center" h="100%">
              <Group gap="xs" align="center">
                <IconTrendingUp size={20} style={{ color: PRIORITY_COLORS.High }} />
                <Text fw={700} size="xl" style={{ color: PRIORITY_COLORS.High }}>
                  {totals.priorities?.High || 0}
                </Text>
              </Group>
              <Text size="sm" ta="center" c="dimmed">High Priority</Text>
              <Text size="xs" ta="center" style={{ color: PRIORITY_COLORS.High }}>Plan mitigation soon</Text>
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Card withBorder radius="md" p="md" h={120}>
            <Stack gap="xs" align="center" justify="center" h="100%">
              <Group gap="xs" align="center">
                <IconShield size={20} color="#339af0" />
                <Text fw={700} size="xl" c="blue">
                  {totals.risks}
                </Text>
              </Group>
              <Text size="sm" ta="center" c="dimmed">Total Risks</Text>
              <Text size="xs" ta="center" c="dimmed">Across all areas</Text>
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Card withBorder radius="md" p="md" h={120}>
            <Stack gap="xs" align="center" justify="center" h="100%">
              <Group gap="xs" align="center">
                <IconShieldCheck size={20} color="#2f9e44" />
                <Text fw={700} size="xl" c="green">
                  {totals.actions}
                </Text>
              </Group>
              <Text size="sm" ta="center" c="dimmed">Actions</Text>
              <Text size="xs" ta="center" c="dimmed">{totals.completedActions} completed</Text>
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>

      {/* Charts Row */}
      <Grid>
        {/* Risk Priority Distribution - Bar Chart */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card withBorder radius="md" p="lg" h={400}>
            <Stack gap="md" h="100%">
              <Group justify="space-between" align="center">
                <Text fw={600} size="lg">Risks by Priority</Text>
                <Badge variant="light" color="blue" size="sm">
                  {priorityChartData.length} levels
                </Badge>
              </Group>
              <div style={{ flex: 1, minHeight: 250 }}>
                {priorityChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={priorityChartData} margin={{ top: 20, right: 20, left: 20, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                      <XAxis
                        dataKey="name"
                        angle={-45}
                        textAnchor="end"
                        interval={0}
                        height={60}
                        fontSize={12}
                      />
                      <YAxis allowDecimals={false} fontSize={12} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {priorityChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Stack align="center" justify="center" h="100%">
                    <Text c="dimmed">No risks to display</Text>
                  </Stack>
                )}
              </div>
            </Stack>
          </Card>
        </Grid.Col>

        {/* Risk Areas Breakdown - Pie Chart */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card withBorder radius="md" p="lg" h={400}>
            <Stack gap="md" h="100%">
              <Group justify="space-between" align="center">
                <Text fw={600} size="lg">Risks by Area</Text>
                <Badge variant="light" color="green" size="sm">
                  {areaDistributionData.length} areas
                </Badge>
              </Group>
              <div style={{ flex: 1, minHeight: 250 }}>
                {areaDistributionData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={areaDistributionData}
                        dataKey="value"
                        nameKey="name"
                        outerRadius={90}
                        innerRadius={40}
                        paddingAngle={2}
                        label={({ name, value }) => `${name}: ${value}`}
                        labelLine={false}
                        fontSize={11}
                      >
                        {areaDistributionData.map((entry, index) => (
                          <Cell key={`cell-pie-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Legend
                        layout="horizontal"
                        verticalAlign="bottom"
                        align="center"
                        wrapperStyle={{ paddingTop: 20 }}
                      />
                      <RechartsTooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Stack align="center" justify="center" h="100%">
                    <Text c="dimmed">No risks to display</Text>
                  </Stack>
                )}
              </div>
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>

      {/* Mitigation Progress */}
      <Card withBorder radius="md" p="lg">
        <Stack gap="md">
          <Group justify="space-between" align="center">
            <Text fw={600} size="lg">Mitigation Progress by Area</Text>
            <Badge
              variant="light"
              color={totals.progress >= 70 ? 'green' : totals.progress >= 40 ? 'orange' : 'red'}
              size="lg"
            >
              {totals.progress}% Overall
            </Badge>
          </Group>
          <Grid>
            {Object.entries(riskAreas).map(([areaKey, areaConfig]) => {
              const stats = byArea[areaKey] || { riskCount: 0, actionCount: 0, completedActions: 0, progress: 0 };
              if (stats.riskCount === 0) return null;

              return (
                <Grid.Col span={{ base: 12, sm: 6, md: 4 }} key={areaKey}>
                  <Paper withBorder p="md" radius="md">
                    <Stack gap="xs">
                      <Group justify="space-between">
                        <Text size="sm" fw={500}>{areaConfig.label}</Text>
                        <Badge size="sm" variant="light" color={areaConfig.color}>
                          {stats.riskCount} risks
                        </Badge>
                      </Group>
                      <Group justify="space-between">
                        <Text size="xs" c="dimmed">
                          {stats.completedActions}/{stats.actionCount} actions
                        </Text>
                        <Text size="xs" fw={500} c={
                          stats.progress >= 70 ? 'green' : stats.progress >= 40 ? 'orange' : 'dimmed'
                        }>
                          {stats.progress}%
                        </Text>
                      </Group>
                      <div
                        style={{
                          height: 6,
                          backgroundColor: '#e9ecef',
                          borderRadius: 3,
                          overflow: 'hidden'
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            width: `${stats.progress}%`,
                            backgroundColor: stats.progress >= 70 ? '#2f9e44' : stats.progress >= 40 ? '#f59f00' : '#adb5bd',
                            borderRadius: 3,
                            transition: 'width 0.3s ease'
                          }}
                        />
                      </div>
                    </Stack>
                  </Paper>
                </Grid.Col>
              );
            })}
          </Grid>
        </Stack>
      </Card>
    </Stack>
  );
}

ExplorerChartsSummary.propTypes = {
  areaStats: PropTypes.shape({
    byArea: PropTypes.object.isRequired,
    totals: PropTypes.shape({
      risks: PropTypes.number.isRequired,
      actions: PropTypes.number.isRequired,
      completedActions: PropTypes.number.isRequired,
      progress: PropTypes.number.isRequired,
      priorities: PropTypes.object.isRequired
    }).isRequired
  }).isRequired,
  explorerRisks: PropTypes.object,
  riskAreas: PropTypes.object.isRequired,
  savedControls: PropTypes.object,
  modelId: PropTypes.string
};
